import React from "react";

export type EventActionIconType =
  | "COMMENT"
  | "LIKE"
  | "FOLLOW"
  | "SHARE"
  | "CREATE_CONTENT"
  | "DAILY_CHECKIN"
  | "JOIN_A_CHALLENGE"
  | "UNLOCK_EPISODE";

const normalizeType = (value: string): EventActionIconType | null => {
  switch (value) {
    case "COMMENT":
      return "COMMENT";
    case "LIKE":
      return "LIKE";
    case "FOLLOW":
      return "FOLLOW";
    case "SHARE":
      return "SHARE";
    case "CREATE_CONTENT":
    case "CONTENT_CREATION":
    case "CREATE":
      return "CREATE_CONTENT";
    case "DAILY_CHECKIN":
    case "DAILY_CHECK_IN":
      return "DAILY_CHECKIN";
    case "JOIN_A_CHALLENGE":
    case "CHALLENGE":
      return "JOIN_A_CHALLENGE";
    case "UNLOCK_EPISODE":
    case "UNBLOCK_EPISODE":
      return "UNLOCK_EPISODE";
    default:
      return null;
  }
};

export const resolveEventActionIconType = (
  value?: string | null,
): EventActionIconType | null => {
  const normalizedValue = String(value || "").trim().toUpperCase();
  if (!normalizedValue) return null;
  return normalizeType(normalizedValue);
};

type EventActionIconProps = {
  type?: string | null;
  className?: string;
};

const EventActionIcon = ({ type, className }: EventActionIconProps) => {
  const resolvedType = resolveEventActionIconType(type);
  if (!resolvedType) return null;

  const classes = ["gi-event-action-icon", className].filter(Boolean).join(" ");
  const dailyCheckinClipId = React.useId();

  switch (resolvedType) {
    case "COMMENT":
      return (
        <svg className={classes} viewBox="0 0 50 50" fill="none" aria-hidden="true">
          <rect width="50" height="50" rx="12" fill="#D4E3ED" />
          <path
            d="M30.3606 15.7104H20.2269C19.1074 15.7118 18.0342 16.1571 17.2426 16.9487C16.451 17.7402 16.0057 18.8135 16.0044 19.9329V26.6829C16.0056 27.6555 16.3419 28.5979 16.9566 29.3515C17.5713 30.1051 18.4269 30.6239 19.3794 30.8204V33.438C19.3795 33.5908 19.4211 33.7409 19.4997 33.872C19.5784 34.0031 19.6911 34.1104 19.8259 34.1825C19.9608 34.2546 20.1126 34.2888 20.2654 34.2814C20.4181 34.274 20.5659 34.2253 20.6931 34.1404L25.5469 30.9104H30.3606C31.4801 30.9091 32.5534 30.4638 33.3449 29.6722C34.1365 28.8807 34.5818 27.8074 34.5831 26.6879V19.9379C34.5831 18.8176 34.1384 17.7431 33.3467 16.9504C32.555 16.1578 31.481 15.7118 30.3606 15.7104ZM28.6706 25.8442H21.9206C21.807 25.8486 21.6937 25.83 21.5874 25.7895C21.4812 25.749 21.3842 25.6875 21.3022 25.6087C21.2203 25.5299 21.1551 25.4353 21.1106 25.3307C21.0661 25.2261 21.0432 25.1135 21.0432 24.9998C21.0432 24.8861 21.0661 24.7736 21.1106 24.669C21.1551 24.5643 21.2203 24.4698 21.3022 24.3909C21.3842 24.3121 21.4812 24.2506 21.5874 24.2101C21.6937 24.1697 21.807 24.1511 21.9206 24.1554H28.6706C28.7843 24.1511 28.8976 24.1697 29.0039 24.2101C29.1101 24.2506 29.2071 24.3121 29.289 24.3909C29.371 24.4698 29.4362 24.5643 29.4807 24.669C29.5252 24.7736 29.5481 24.8861 29.5481 24.9998C29.5481 25.1135 29.5252 25.2261 29.4807 25.3307C29.4362 25.4353 29.371 25.5299 29.289 25.6087C29.2071 25.6875 29.1101 25.749 29.0039 25.7895C28.8976 25.83 28.7843 25.8486 28.6706 25.8442ZM30.3594 22.4692H20.2269C20.0086 22.4608 19.802 22.3682 19.6505 22.2107C19.499 22.0533 19.4144 21.8433 19.4144 21.6248C19.4144 21.4063 19.499 21.1964 19.6505 21.0389C19.802 20.8815 20.0086 20.7889 20.2269 20.7804H30.3606C30.579 20.7889 30.7855 20.8815 30.937 21.0389C31.0885 21.1964 31.1732 21.4063 31.1732 21.6248C31.1732 21.8433 31.0885 22.0533 30.937 22.2107C30.7855 22.3682 30.5777 22.4608 30.3594 22.4692Z"
            fill="#596970"
          />
        </svg>
      );
    case "LIKE":
      return (
        <svg className={classes} viewBox="0 0 55 55" fill="none" aria-hidden="true">
          <rect width="54.2275" height="54.2275" rx="11.1236" fill="#D4E3ED" />
          <path
            d="M26.2248 18.2583C29.0091 15.7585 33.3117 15.8415 35.9941 18.5286C38.6752 21.2169 38.7677 25.4982 36.2738 28.2907L26.2224 38.3563L16.1735 28.2907C13.6796 25.4982 13.7732 21.2097 16.4532 18.5286C19.1379 15.8451 23.4322 15.755 26.2248 18.2583Z"
            fill="#596970"
          />
        </svg>
      );
    case "FOLLOW":
      return (
        <svg className={classes} viewBox="0 0 50 50" fill="none" aria-hidden="true">
          <rect width="50" height="50" rx="12" fill="#D4E3ED" />
          <g opacity="0.9">
            <path
              d="M22.6408 25.6602C25.4457 25.6602 27.7195 23.3864 27.7195 20.5814C27.7195 17.7765 25.4457 15.5027 22.6408 15.5027C19.8358 15.5027 17.562 17.7765 17.562 20.5814C17.562 23.3864 19.8358 25.6602 22.6408 25.6602Z"
              fill="#596970"
            />
            <path
              d="M22.6431 24.3362C20.828 24.3382 19.0878 25.0601 17.8043 26.3436C16.5208 27.6271 15.7989 29.3673 15.7969 31.1824V32.9487C15.7978 33.0872 15.8421 33.2219 15.9235 33.334C16.0049 33.4461 16.1192 33.5299 16.2506 33.5737C18.4713 34.2145 20.7745 34.5237 23.0856 34.4912C25.1428 34.6043 27.2007 34.2797 29.1231 33.5387C29.2333 33.4838 29.3259 33.3992 29.3906 33.2946C29.4553 33.1899 29.4895 33.0692 29.4894 32.9462V31.1824C29.4874 29.3673 28.7655 27.6271 27.482 26.3436C26.1985 25.0601 24.4583 24.3382 22.6431 24.3362Z"
              fill="#596970"
            />
            <path
              d="M30.5925 25.6615C30.5055 25.6619 30.4192 25.6449 30.3387 25.6117C30.2583 25.5784 30.1852 25.5295 30.1238 25.4678L28.3575 23.7003C28.2922 23.6397 28.2398 23.5665 28.2034 23.4852C28.1669 23.4039 28.1473 23.3161 28.1456 23.227C28.1439 23.1379 28.1603 23.0494 28.1936 22.9667C28.2269 22.8841 28.2765 22.809 28.3395 22.746C28.4026 22.683 28.4776 22.6334 28.5603 22.6001C28.6429 22.5667 28.7314 22.5504 28.8205 22.5521C28.9096 22.5538 28.9974 22.5734 29.0787 22.6098C29.1601 22.6462 29.2332 22.6987 29.2938 22.764L30.6538 24.124L33.7326 21.819C33.8731 21.7136 34.0498 21.6683 34.2237 21.6932C34.3977 21.718 34.5546 21.811 34.66 21.9515C34.7655 22.0921 34.8107 22.2688 34.7859 22.4427C34.761 22.6167 34.6681 22.7736 34.5275 22.879L30.9938 25.529C30.879 25.6147 30.7396 25.6612 30.5963 25.6615H30.5925Z"
              fill="#596970"
            />
          </g>
        </svg>
      );
    case "SHARE":
      return (
        <svg className={classes} viewBox="0 0 50 50" fill="none" aria-hidden="true">
          <rect width="50" height="50" rx="12" fill="#D4E3ED" />
          <circle cx="18.5" cy="25" r="3.1" fill="#596970" />
          <circle cx="31.5" cy="18.5" r="3.1" fill="#596970" />
          <circle cx="31.5" cy="31.5" r="3.1" fill="#596970" />
          <path
            d="M21.2 23.8L28.8 20.1M21.2 26.2L28.8 29.9"
            stroke="#596970"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case "CREATE_CONTENT":
      return (
        <svg className={classes} viewBox="0 0 55 55" fill="none" aria-hidden="true">
          <rect width="55" height="55" rx="11.2821" fill="#D4E3ED" />
          <path
            d="M37.1638 26.2591H30.544C30.4408 26.2591 30.3418 26.218 30.2688 26.1451C30.1958 26.0721 30.1548 25.9731 30.1548 25.8698V19.2501C30.1548 18.7337 29.9497 18.2386 29.5846 17.8734C29.2195 17.5083 28.7243 17.3032 28.2079 17.3032C27.6916 17.3032 27.1964 17.5083 26.8313 17.8734C26.4662 18.2386 26.2611 18.7337 26.2611 19.2501V25.8698C26.2611 25.9731 26.2201 26.0721 26.1471 26.1451C26.0741 26.218 25.9751 26.2591 25.8718 26.2591H19.2535C18.7372 26.2591 18.242 26.4642 17.8769 26.8293C17.5118 27.1944 17.3066 27.6896 17.3066 28.2059C17.3066 28.7223 17.5118 29.2174 17.8769 29.5826C18.242 29.9477 18.7372 30.1528 19.2535 30.1528H25.8732C25.9765 30.1528 26.0755 30.1938 26.1485 30.2668C26.2215 30.3398 26.2625 30.4388 26.2625 30.542V37.1603C26.2625 37.6767 26.4676 38.1719 26.8327 38.537C27.1978 38.9021 27.693 39.1072 28.2093 39.1072C28.7257 39.1072 29.2209 38.9021 29.586 38.537C29.9511 38.1719 30.1562 37.6767 30.1562 37.1603V30.5406C30.1562 30.4374 30.1972 30.3384 30.2702 30.2654C30.3432 30.1924 30.4422 30.1514 30.5454 30.1514H37.1638C37.6801 30.1514 38.1753 29.9462 38.5404 29.5811C38.9055 29.216 39.1106 28.7208 39.1106 28.2045C39.1106 27.6882 38.9055 27.193 38.5404 26.8279C38.1753 26.4628 37.6801 26.2591 37.1638 26.2591Z"
            fill="#596970"
          />
        </svg>
      );
    case "DAILY_CHECKIN":
      return (
        <svg className={classes} viewBox="0 0 55 55" fill="none" aria-hidden="true">
          <rect width="54.2275" height="54.2275" rx="11.1236" fill="#D4E3ED" />
          <g clipPath={`url(#${dailyCheckinClipId})`}>
            <path
              d="M27.1139 39.7083C20.158 39.7083 14.5195 34.0698 14.5195 27.1139C14.5195 20.158 20.158 14.5195 27.1139 14.5195C34.0698 14.5195 39.7083 20.158 39.7083 27.1139C39.7083 34.0698 34.0698 39.7083 27.1139 39.7083ZM25.8583 32.1517L34.7625 23.2462L32.9817 21.4653L25.8583 28.59L22.2953 25.027L20.5145 26.8079L25.8583 32.1517Z"
              fill="#596970"
            />
          </g>
          <defs>
            <clipPath id={dailyCheckinClipId}>
              <rect
                width="30.2266"
                height="30.2266"
                fill="white"
                transform="translate(12 12.0005)"
              />
            </clipPath>
          </defs>
        </svg>
      );
    case "JOIN_A_CHALLENGE":
      return (
        <svg className={classes} viewBox="0 0 50 50" fill="none" aria-hidden="true">
          <rect width="50" height="50" rx="12" fill="#D4E3ED" />
          <path
            d="M20.8 17.6H29.2V21.2C29.2 23.5186 27.3186 25.4 25 25.4C22.6814 25.4 20.8 23.5186 20.8 21.2V17.6Z"
            stroke="#596970"
            strokeWidth="2.1"
            strokeLinejoin="round"
          />
          <path
            d="M20.8 19.3H18.7C18.7 21.34 20.12 23.02 22.03 23.45M29.2 19.3H31.3C31.3 21.34 29.88 23.02 27.97 23.45"
            stroke="#596970"
            strokeWidth="2.1"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M25 25.4V30.4M22.2 33H27.8"
            stroke="#596970"
            strokeWidth="2.1"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case "UNLOCK_EPISODE":
      return (
        <svg className={classes} viewBox="0 0 55 55" fill="none" aria-hidden="true">
          <rect width="54.2275" height="54.2275" rx="11.1236" fill="#D4E3ED" />
          <path
            d="M15.5557 17.623C15.5557 16.9735 16.0831 16.446 16.7327 16.446H35.7141C36.3636 16.446 36.8911 16.9735 36.8911 17.623V36.6045C36.8908 36.9165 36.7667 37.2157 36.546 37.4364C36.3253 37.657 36.0261 37.7812 35.7141 37.7815H16.7327C16.4206 37.7812 16.1214 37.657 15.9007 37.4364C15.6801 37.2157 15.556 36.9165 15.5557 36.6045V17.623ZM17.9263 18.8166V35.4109H34.5205V18.8166H17.9263ZM24.59 22.8644L30.3731 26.719C30.4381 26.7623 30.4915 26.821 30.5284 26.8899C30.5653 26.9587 30.5846 27.0356 30.5846 27.1138C30.5846 27.1919 30.5653 27.2688 30.5284 27.3376C30.4915 27.4065 30.4381 27.4652 30.3731 27.5085L24.5888 31.3631C24.5175 31.4103 24.4347 31.4374 24.3493 31.4415C24.2638 31.4455 24.1788 31.4263 24.1034 31.386C24.0279 31.3457 23.9648 31.2856 23.9206 31.2123C23.8765 31.139 23.8531 31.0551 23.8528 30.9695V23.258C23.8529 23.1722 23.8763 23.0881 23.9205 23.0146C23.9647 22.9411 24.028 22.881 24.1036 22.8406C24.1793 22.8002 24.2645 22.7811 24.3501 22.7853C24.4358 22.7895 24.5187 22.8169 24.59 22.8644Z"
            fill="#596970"
          />
        </svg>
      );
    default:
      return null;
  }
};

export default EventActionIcon;
